import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Chip
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ConstructionIcon from '@mui/icons-material/Construction';
import { Permit, SubscriptionPeriod } from './types';
import { selectIsInCart, addToCart, removeFromCart } from 'src/redux-store/slices/cartSlice';

interface PermitCardProps {
  permit: Permit;
}

export const PermitCard: React.FC<PermitCardProps> = ({ permit }) => {
  const dispatch = useDispatch();
  const [selectedPeriod, setSelectedPeriod] = useState<SubscriptionPeriod>('Yearly');
  const isInCart = useSelector(selectIsInCart(permit?.PermitID));

  const price = permit?.SubscriptionFee[selectedPeriod === 'Yearly' ? 'Yearly' : 'Monthly'];

  const isSubscribed = permit?.TownshipSubscription?.IsSubscribed;
  const subscriptionEndDate = permit?.TownshipSubscription?.SubscriptionEndDate;
  const isInactive = permit?.Status !== 'Active';

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePeriodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newPeriod: SubscriptionPeriod | null
  ) => {
    if (newPeriod !== null && !isInCart && !isSubscribed && !isInactive) {
      setSelectedPeriod(newPeriod);
    }
  };

  const handleSubscribe = () => {
    dispatch(
      addToCart({
        PermitID: permit?.PermitID,
        PermitName: permit?.PermitName,
        Period: selectedPeriod,
        Price: permit?.SubscriptionFee[selectedPeriod === 'Yearly' ? 'Yearly' : 'Monthly']
      })
    );
  };

  const handleRemove = () => {
    dispatch(removeFromCart({ permitId: permit?.PermitID }));
  };

  const cardStyles = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    position: 'relative',
    boxSizing: 'border-box',
    border: '.5px solid #059EE3',
    opacity: isInactive ? 0.7 : 1,
    ...(isInCart
      ? {
          border: '2px solid #059EE3',
          boxShadow: '0 8px 16px rgba(5, 158, 227, 0.15)'
        }
      : {
          '&:hover': {
            boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
            boxSizing: 'border-box'
          }
        }),
    ...(isSubscribed && {
      border: '2px solid #4CAF50',
      boxShadow: '0 8px 16px rgba(76, 175, 80, 0.15)'
    })
  };

  return (
    <Card sx={cardStyles}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: '700',
          backgroundColor: isInactive ? 'grey.500' : isSubscribed ? 'success.main' : 'primary.main',
          padding: '15px 10px',
          color: 'white',
          borderRadius: '10px 10px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        mb={2}
        component="h3"
      >
        {permit?.PermitName}
        {isSubscribed && (
          <Chip
            label="Subscribed"
            size="small"
            sx={{
              backgroundColor: 'white',
              color: 'success.main',
              fontWeight: '600'
            }}
          />
        )}
      </Typography>

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {isInactive ? (
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <ConstructionIcon sx={{ fontSize: 40, color: 'grey.500', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: '500' }}>
              This permit is currently unavailable for subscription
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please check back later
            </Typography>
          </Box>
        ) : isSubscribed ? (
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <Typography variant="body1" color="success.main" sx={{ fontWeight: '500', mb: 1 }}>
              Your subscription is active
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Valid until: {formatDate(subscriptionEndDate)}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <ToggleButtonGroup
                value={selectedPeriod}
                exclusive
                onChange={handlePeriodChange}
                aria-label="subscription period"
                size="small"
                disabled={isInCart}
              >
                <ToggleButton value="Monthly" aria-label="Monthly" sx={{ px: 1.5 }}>
                  Monthly
                </ToggleButton>
                <ToggleButton value="Yearly" aria-label="Yearly" sx={{ px: 1.5 }}>
                  Yearly
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ my: 2, textAlign: 'center' }}>
              {price === 0 ? (
                <Typography variant="h5" color="primary">
                  Free
                </Typography>
              ) : (
                <Typography variant="h3">
                  ${price}
                  <Typography variant="subtitle2" component="span">
                    /{selectedPeriod === 'Yearly' ? 'year' : 'month'}
                  </Typography>
                </Typography>
              )}
            </Box>
          </>
        )}

        {!isInactive && !isSubscribed &&
          (isInCart ? (
            <Button
              variant="outlined"
              color="error"
              fullWidth
              sx={{ mt: 3 }}
              onClick={handleRemove}
              startIcon={<ShoppingCartIcon />}
            >
              Remove from Cart
            </Button>
          ) : (
            <Button variant="contained" fullWidth sx={{ mt: 3 }} onClick={handleSubscribe}>
              {price === 0 ? 'Get Started' : 'Subscribe Now'}
            </Button>
          ))}
      </CardContent>
    </Card>
  );
};
